import { useNavigate } from 'react-router-dom';
import { CaretDown, Check, Plus } from '@phosphor-icons/react';
import { useT } from '../i18n/I18nContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

// Replaces the old sidebar project list: the trigger is the current project's
// name (or "Projects" off-board), the menu switches projects or creates one.
export default function ProjectSwitcher({ projects, currentProject, onNewProject }) {
  const nav = useNavigate();
  const t = useT();

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="project-switcher"
          className="inline-flex items-center gap-1.5 min-w-0 max-w-[280px] -mx-1.5 px-1.5 py-0.5 rounded-sm hover:bg-zinc-100 outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
        >
          {currentProject ? (
            <h1 className="font-display text-2xl font-black tracking-tight leading-tight truncate" data-testid="project-name">
              {currentProject.name}
            </h1>
          ) : (
            <span className="font-display font-black tracking-tight text-base">{t('sidebar.projects')}</span>
          )}
          <CaretDown size={14} weight="bold" className="flex-shrink-0 text-zinc-500" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="z-[100] w-64 rounded-sm" data-testid="projects-list">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">
          {t('sidebar.projects')}
        </DropdownMenuLabel>
        {projects.length === 0 ? (
          <div className="px-2 py-2 text-xs text-zinc-500">{t('sidebar.noProjects')}</div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            {projects.map((p) => {
              const active = currentProject?.id === p.id;
              return (
                <DropdownMenuItem
                  key={p.id}
                  data-testid={`project-item-${p.id}`}
                  onSelect={() => nav(`/projects/${p.id}`)}
                  className={`rounded-sm text-sm gap-2 ${active ? 'font-semibold' : ''}`}
                >
                  <Check size={12} weight="bold" className={active ? 'text-zinc-900' : 'invisible'} />
                  <span className="truncate flex-1">{p.name}</span>
                  {p.ticket_count > 0 && (
                    <span
                      data-testid={`project-ticket-count-${p.id}`}
                      className="flex-shrink-0 font-mono text-[10px] tabular-nums text-zinc-400"
                    >
                      {p.ticket_count}
                    </span>
                  )}
                </DropdownMenuItem>
              );
            })}
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="new-project-btn"
          onSelect={onNewProject}
          className="rounded-sm text-sm gap-2"
        >
          <Plus size={12} weight="bold" />
          {t('sidebar.newProject')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
